// @oasis-permission-inherits: /capcode (capcode:view)
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { listTestReportsQuery } from "~/modules/test-runs/queries";

const CAPTIONS_WHISPER_COMMIT_URL = "https://github.com/bungeeapp/captions-whisper/commit";

const searchSchema = z.object({
  commitHash: z.string().optional(),
  tag: z.string().optional(),
});

type TestRunsSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_dash/capcode/test-runs/")({
  validateSearch: searchSchema,
  component: RouteComponent,
  loaderDeps: ({ search }) => ({ commitHash: search.commitHash, tag: search.tag }),
  async loader({ context, deps }) {
    const { queryClient } = context;
    await queryClient.prefetchQuery(
      listTestReportsQuery.queryOptions({
        params: {
          commitHash: deps.commitHash,
          tag: deps.tag,
        },
      })
    );
  },
});

function formatDate(timestamp: number) {
  return new Date(timestamp).toLocaleString();
}

function RouteComponent() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const router = useRouter();

  const { data, isLoading, error, isFetching } = listTestReportsQuery.useQuery({
    params: { commitHash: search.commitHash, tag: search.tag },
  });

  const reports = data?.reports ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">Test Runs</h1>
        <span className="text-xs text-muted-foreground">
          {reports.length} {reports.length === 1 ? "report" : "reports"}
        </span>
      </div>

      <TestRunsFilterBar
        search={search}
        onApply={(next) => navigate({ search: next })}
        isFetching={isFetching}
      />

      {error && (
        <div className="rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
          <p className="font-medium">Failed to load test reports</p>
          <pre className="mt-2 whitespace-pre-wrap text-xs">{error.message}</pre>
        </div>
      )}

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-md" />
          ))}
        </div>
      )}

      {!isLoading && !error && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Created At</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Commit</TableHead>
              <TableHead>Results</TableHead>
              <TableHead>Tags</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reports.map((report) => (
              <TableRow
                key={report.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() =>
                  router.navigate({
                    to: "/capcode/test-runs/$reportId",
                    params: { reportId: report.id },
                    search,
                  })
                }
              >
                <TableCell className="whitespace-nowrap">{formatDate(report.createdAt)}</TableCell>
                <TableCell>
                  <div className="flex flex-col">
                    <span className="font-medium">{report.name}</span>
                    {report.description && (
                      <span className="text-xs text-muted-foreground line-clamp-1">
                        {report.description}
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <a
                    href={`${CAPTIONS_WHISPER_COMMIT_URL}/${report.commitHash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-xs text-blue-600 hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {report.commitHash.slice(0, 7)}
                  </a>
                </TableCell>
                <TableCell>
                  <ResultsBadge
                    passed={report.passedCount}
                    failed={report.failedCount}
                    total={report.totalCount}
                  />
                </TableCell>
                <TableCell>
                  <TagList tags={report.tags} />
                </TableCell>
              </TableRow>
            ))}
            {reports.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground">
                  No test reports found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function ResultsBadge({
  passed,
  failed,
  total,
}: {
  passed: number;
  failed: number;
  total: number;
}) {
  const hasFailures = failed > 0;
  return (
    <div className="flex items-center gap-1">
      <Badge variant={hasFailures ? "destructive" : "default"} className="text-xs">
        {passed}/{total} passed
      </Badge>
      {hasFailures && (
        <Badge variant="outline" className="text-xs text-destructive border-destructive/40">
          {failed} failed
        </Badge>
      )}
    </div>
  );
}

function TagList({ tags }: { tags: string[] | null | undefined }) {
  if (!tags || tags.length === 0) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {tags.map((tag) => (
        <Badge key={tag} variant="outline" className="text-xs">
          {tag}
        </Badge>
      ))}
    </div>
  );
}

function TestRunsFilterBar({
  search,
  onApply,
  isFetching,
}: {
  search: TestRunsSearch;
  onApply: (next: TestRunsSearch) => void;
  isFetching: boolean;
}) {
  const [commitHash, setCommitHash] = useState(search.commitHash ?? "");
  const [tag, setTag] = useState(search.tag ?? "");

  useEffect(() => {
    setCommitHash(search.commitHash ?? "");
  }, [search.commitHash]);

  useEffect(() => {
    setTag(search.tag ?? "");
  }, [search.tag]);

  const apply = () => {
    onApply({
      commitHash: commitHash.trim() || undefined,
      tag: tag.trim() || undefined,
    });
  };

  const clear = () => {
    setCommitHash("");
    setTag("");
    onApply({});
  };

  const hasActiveFilters = Boolean(search.commitHash || search.tag);

  return (
    <form
      className="flex flex-wrap items-end gap-3 rounded-md border bg-muted/30 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        apply();
      }}
    >
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-muted-foreground" htmlFor="commit-hash-filter">
          Commit hash
        </label>
        <Input
          id="commit-hash-filter"
          value={commitHash}
          onChange={(e) => setCommitHash(e.target.value)}
          placeholder="Full commit hash"
          className="h-8 w-72 font-mono text-xs"
        />
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-muted-foreground" htmlFor="tag-filter">
          Tag
        </label>
        <Input
          id="tag-filter"
          value={tag}
          onChange={(e) => setTag(e.target.value)}
          placeholder="e.g. fennec"
          className="h-8 w-48 text-xs"
        />
      </div>
      <div className="flex items-center gap-2">
        <Button type="submit" size="sm" disabled={isFetching}>
          Apply
        </Button>
        {hasActiveFilters && (
          <Button type="button" size="sm" variant="ghost" onClick={clear}>
            Clear
          </Button>
        )}
      </div>
    </form>
  );
}
